import React, {createContext, useContext, useReducer, useMemo, useCallback} from 'react';
import {validateParking} from './utils';

import useSafeDispatch from 'hooks/useSafeDispatch';

import {actionTypes, deskBookingReducer, initialState} from './deskBookingReducer';
import {useAuthFetch} from '../fetch-context';
import {checkCountryIsBelgium} from '../../utils/belgian';

const DeskBookingContext = createContext();
DeskBookingContext.displayName = 'DeskBookingContext';

const DeskBookingDispatch = createContext();
DeskBookingDispatch.displayName = 'DeskBookingDispatch';

const DeskBookingProvider = ({children, reducer = deskBookingReducer}) => {
  const [state, unsafeDispatch] = useReducer(reducer, initialState);

  const memoizedState = useMemo(() => state, [state]);
  const dispatch = useSafeDispatch(unsafeDispatch);

  return (
    <DeskBookingContext.Provider value={memoizedState}>
      <DeskBookingDispatch.Provider value={{dispatch}}>{children}</DeskBookingDispatch.Provider>
    </DeskBookingContext.Provider>
  );
};

function useDeskBookingState() {
  const context = useContext(DeskBookingContext);

  if (context === undefined) {
    throw new Error('useDeskBookingState must be used within a DeskBookingProvider');
  }

  const {
    selectedPositionIds,
    selectedDates,
    selectedFacilityId,
    bookedPosition,
    bookedDates,
    bookedParking,
    selectedPosition,
    selectedParking,
    // selectedType,
    datesAndType
  } = context;

  const {account} = useAuthFetch();
  const {countries} = account;

  const isBelgian = checkCountryIsBelgium(countries);

  // deskbooking context selectors

  const isSelectedDeskId = (id) => id === selectedPosition?.id;
  const externalParkingSupported = selectedParking[0]?.externalParkingSupported ?? false;
  const isParkingSelected = selectedParking[0]?.checked ?? false;
  const isExternalParkingSelected = selectedParking[0]?.externalParkingChecked ?? false;
  const plateNumber = selectedParking[0]?.plateNumber ?? '';
  const parkingIsValid = validateParking(selectedParking, isBelgian);

  const deskBookingDto = {
    facilityId: selectedFacilityId,
    positionId: selectedPositionIds[0],
    x: selectedPosition?.x,
    y: selectedPosition?.y,
    code: selectedPosition?.code
  };

  const createDeskBookingDto = {
    ...deskBookingDto,
    dates: selectedDates,
    parking: selectedParking.map((selectedParkingItem) => ({
      parkingRequested: !selectedParkingItem.externalParkingSupported
        ? selectedParkingItem.checked
        : selectedParkingItem.checked || selectedParkingItem.externalParkingChecked,
      plateNumber: selectedParkingItem.plateNumber,
      type: !selectedParkingItem.externalParkingSupported
        ? 'All'
        : selectedParkingItem.checked && selectedParkingItem.externalParkingChecked
        ? 'All'
        : selectedParkingItem.checked
        ? 'Internal'
        : selectedParkingItem.externalParkingChecked
        ? 'External'
        : null
    })),
    datesAndType: datesAndType
  };

  const editDeskBookingDto = {
    ...deskBookingDto,
    date: selectedDates[0],
    parking: {
      parkingRequested: isParkingSelected || isExternalParkingSelected,
      plateNumber,
      type: !externalParkingSupported
        ? 'All'
        : isParkingSelected && isExternalParkingSelected
        ? 'All'
        : isParkingSelected
        ? 'Internal'
        : isExternalParkingSelected
        ? 'External'
        : null
    },
    datesAndType: datesAndType[0]
  };
  const bookingHasChanged = (() => {
    let datesChanged = false;
    let facilityChanged = false;
    let positionChanged = false;
    let parkingChanged = false;
    let worktypesChanged = false;

    if (!bookedDates.includes(selectedDates[0])) {
      datesChanged = true;
    }

    if (bookedPosition && selectedFacilityId !== bookedPosition.facilityId) {
      facilityChanged = true;
    }

    if (bookedPosition && selectedPositionIds && !selectedPositionIds.includes(bookedPosition.id)) {
      positionChanged = true;
    }

    if (
      bookedParking[0]?.checked !== isParkingSelected ||
      bookedParking[0]?.externalParkingChecked !== isExternalParkingSelected ||
      bookedParking[0]?.plateNumber !== plateNumber
    ) {
      parkingChanged = true;
    }

    if (datesAndType?.length && editDeskBookingDto?.datesAndType?.date !== null) {
      worktypesChanged = true;
    }

    return datesChanged || facilityChanged || positionChanged || parkingChanged || worktypesChanged;
  })();

  return {
    selectedPositionIds,
    isSelectedDeskId,
    createDeskBookingDto,
    editDeskBookingDto,
    bookingHasChanged,
    parkingIsValid,
    datesAndType,
    isBelgian,
    ...context
  };
}

function useDeskBookingDispatch() {
  const context = useContext(DeskBookingDispatch);

  if (context === undefined) {
    throw new Error('useDeskBookingDispatch must be used within a DeskBookingProvider');
  }

  const {dispatch} = context;
  const initDeskbooking = useCallback(() => dispatch({type: actionTypes.init}), [dispatch]);
  const setSelectedDates = useCallback(
    (payload) => dispatch({type: actionTypes.setSelectedDates, payload}),
    [dispatch]
  );
  const setLoading = useCallback((payload) => dispatch({type: actionTypes.setLoading, payload}), [
    dispatch
  ]);
  const setBuilding = useCallback(
    (building) => dispatch({type: actionTypes.setBuilding, payload: {building}}),
    [dispatch]
  );
  const setFacility = useCallback(
    (floorId, building) => dispatch({type: actionTypes.setFacility, payload: {floorId, building}}),
    [dispatch]
  );
  const togglePositionId = useCallback(
    (payload) => dispatch({type: actionTypes.togglePositionId, payload}),
    [dispatch]
  );
  const setBookingDetails = useCallback(
    (payload) => dispatch({type: actionTypes.setBookingDetails, payload}),
    [dispatch]
  );
  const toggleSelectedDate = useCallback(
    (payload) => dispatch({type: actionTypes.toggleSelectedDate, payload}),
    [dispatch]
  );
  const onSelectParking = useCallback(
    (date, field) => dispatch({type: actionTypes.onSelectParking, payload: {date, field}}),
    [dispatch]
  );
  const onSetParkingPlate = useCallback(
    (parkingPlate) => dispatch({type: actionTypes.onSetParkingPlate, payload: parkingPlate}),
    [dispatch]
  );
  const selectedType = useCallback(
    (payload) => dispatch({type: actionTypes.SELECT_TYPE, payload}),
    [dispatch]
  );

  return {
    dispatch,
    setSelectedDates,
    setLoading,
    setBuilding,
    setFacility,
    togglePositionId,
    initDeskbooking,
    setBookingDetails,
    toggleSelectedDate,
    onSelectParking,
    onSetParkingPlate,
    selectedType
  };
}

export {DeskBookingProvider, useDeskBookingState, useDeskBookingDispatch};
